// src/components/fix-actions/redesign/agent-thread.tsx
//
// Upmode AI agent thread - the tab-aware briefing at the top of the module.
// Two modes: "move" (a recommended action + through-line chips) and "search"
// (a prompt input + suggested chips, used on the Action Log tab).
//
// The shell computes the model from real data and passes it in; this
// component is purely presentational.

import { Ic, SPARKLE } from "./shared";

export interface AgentChip {
  label: string;
  onSelect: () => void;
}

export interface AgentThreadModel {
  mode: "move" | "search";
  status: string;
  lead: string;
  rest: string;
  // move mode
  moveLabel?: string;
  moveMeta?: string;
  moveTitle?: string;
  moveRationale?: string;
  moveCta?: string;
  onRunMove?: () => void;
  thenVerb?: string;
  thenChips?: AgentChip[];
  // search mode
  searchPlaceholder?: string;
  searchChips?: AgentChip[];
  onSearchSubmit?: (value: string) => void;
}

export function AgentThread({ model }: { model: AgentThreadModel }) {
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        padding: "16px clamp(16px,2vw,24px) 18px",
        borderBottom: "1px solid var(--border)",
        background: "linear-gradient(135deg,var(--spectral-violet-10),var(--alpha-teal-5) 56%,var(--card))",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "-32%",
          right: "-5%",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle,var(--spectral-violet-10),transparent 66%)",
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", display: "flex", alignItems: "flex-start", gap: 14 }}>
        <span
          className="ai-orb"
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: "linear-gradient(135deg,var(--spectral-violet) 0%,var(--spectral-violet) 62%,var(--signal-blue) 155%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          <Ic paths={SPARKLE} size={18} sw={1.7} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--spectral-violet)" }}>Upmode AI</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 500, color: "var(--muted-foreground)" }}>
              <span style={{ width: 6, height: 6, borderRadius: 9999, background: "var(--signal-green)" }} />
              {model.status}
            </span>
          </div>

          <p
            style={{
              fontSize: 17,
              fontWeight: 500,
              letterSpacing: "-.01em",
              lineHeight: 1.45,
              color: "var(--foreground)",
              margin: "0 0 12px",
            }}
          >
            {model.lead} <span style={{ color: "var(--muted-foreground)" }}>{model.rest}</span>
          </p>

          {model.mode === "move" && (
            <>
              <div
                style={{
                  background: "var(--card)",
                  border: "1px solid var(--spectral-violet-20)",
                  borderRadius: 12,
                  padding: "13px 15px",
                  marginBottom: 12,
                  boxShadow: "var(--elevation-sm)",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--spectral-violet)" }}>
                      {model.moveLabel}
                    </span>
                    <span style={{ width: 3, height: 3, borderRadius: 9999, background: "var(--silver-mist)" }} />
                    <span style={{ fontSize: 11.5, color: "var(--muted-foreground)" }}>{model.moveMeta}</span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-.01em", color: "var(--foreground)", lineHeight: 1.35, marginBottom: 3 }}>
                    {model.moveTitle}
                  </div>
                  <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.5, margin: 0 }}>{model.moveRationale}</p>
                </div>
                <button
                  onClick={model.onRunMove}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    alignSelf: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#fff",
                    background: "linear-gradient(135deg,var(--spectral-violet) 0%,var(--spectral-violet) 62%,var(--signal-blue) 155%)",
                    border: "none",
                    borderRadius: 9,
                    padding: "9px 17px",
                    cursor: "pointer",
                    boxShadow: "var(--shadow-spectral-violet-glow)",
                    fontFamily: "inherit",
                  }}
                >
                  <Ic paths={SPARKLE} size={15} sw={1.8} />
                  {model.moveCta}
                </button>
              </div>

              {model.thenChips && model.thenChips.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: "var(--muted-foreground)" }}>
                  <span>{model.thenVerb}</span>
                  {model.thenChips.map((c, i) => (
                    <button
                      key={i}
                      className="ai-chip"
                      onClick={c.onSelect}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        height: 28,
                        padding: "0 12px",
                        borderRadius: 9999,
                        background: "var(--card)",
                        border: "1px solid var(--border)",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "var(--foreground)",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {model.mode === "search" && (
            <div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const input = (e.currentTarget.elements.namedItem("agentq") as HTMLInputElement | null);
                  model.onSearchSubmit?.(input?.value || "");
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: "var(--background)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  padding: "0 10px 0 18px",
                  height: 56,
                  marginBottom: 14,
                  boxShadow: "0 0 0 4px var(--spectral-violet-10)",
                }}
              >
                <input
                  name="agentq"
                  className="ai-input"
                  type="text"
                  placeholder={model.searchPlaceholder}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: 16,
                    fontFamily: "var(--font-sans)",
                    color: "var(--foreground)",
                  }}
                />
                <button
                  type="submit"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    border: "none",
                    background: "linear-gradient(135deg,var(--spectral-violet) 0%,var(--spectral-violet) 62%,var(--signal-blue) 155%)",
                    color: "#fff",
                    cursor: "pointer",
                    flexShrink: 0,
                    boxShadow: "var(--shadow-spectral-violet-glow)",
                  }}
                >
                  <Ic paths={["M5 12h14M13 6l6 6-6 6"]} size={18} sw={2} />
                </button>
              </form>
              {model.searchChips && model.searchChips.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>Suggested:</span>
                  {model.searchChips.map((c, i) => (
                    <button
                      key={i}
                      className="ai-chip"
                      onClick={c.onSelect}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        height: 34,
                        padding: "0 14px",
                        borderRadius: 9999,
                        background: "var(--card)",
                        border: "1px solid var(--border)",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "var(--foreground)",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 9999, background: "var(--signal-blue)", flexShrink: 0 }} />
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
